import { z } from 'zod';

import { PrincipalModificationId, WorkoutPlanId } from '../BrandedIds';
import { zodJSInstantDate, zodJSPlainDate } from '../dates/BrandedDate';

export const ModificationStatus = z.enum(['active', 'cancelled']);

export type ModificationStatus = z.infer<typeof ModificationStatus>;

export const ModificationBase = z.object({
  id: PrincipalModificationId,
  effectiveDate: zodJSPlainDate,
  status: ModificationStatus,
  createdAt: zodJSInstantDate,
  updatedAt: zodJSInstantDate,
  cancelledAt: zodJSInstantDate.optional(),
});

export const InitialModification = ModificationBase.extend({
  type: z.literal('initial'),
  amount: z.number(),
  interestRate: z.number(),
  loanTerm: z.number(),
});

export const PrincipalModification = ModificationBase.extend({
  type: z.literal('principalOnly'),
  amount: z.number(), // Difference from principal balance at effective date
  collectionType: z.enum(['waived', 'deferred']),
  isRecast: z.boolean(),
  isFederalGovernmentPlan: z.boolean(),
});

export const FlexModification = ModificationBase.extend({
  type: z.literal('flexModification'),
  unpaidPrincipalBalance: z.number(), // New unpaid principal balance at effective date
  interestRate: z.number(),
  loanTerm: z.number(),
  deferredPrincipalAmount: z.number(),
  workoutPlanId: WorkoutPlanId.optional(),
});

export const Modification = z.discriminatedUnion('type', [
  InitialModification,
  PrincipalModification,
  FlexModification,
]);

export type Modification = z.infer<typeof Modification>;

export type InitialModification = z.infer<typeof InitialModification>;

export type PrincipalModification = z.infer<typeof PrincipalModification>;

export type FlexModification = z.infer<typeof FlexModification>;

export const PrincipalModificationInput = PrincipalModification.omit({
  id: true,
  type: true,
  createdAt: true,
  updatedAt: true,
  status: true,
});

export type PrincipalModificationInput = z.infer<typeof PrincipalModificationInput>;

export const FlexModificationInput = FlexModification.omit({
  id: true,
  type: true,
  createdAt: true,
  updatedAt: true,
  status: true,
});

export type FlexModificationInput = z.infer<typeof FlexModificationInput>;

export const isPrincipalModification = (modification: Modification): modification is PrincipalModification =>
  modification.type === 'principalOnly';

export const isFlexModification = (modification: Modification): modification is FlexModification =>
  modification.type === 'flexModification';

export const isInitialModification = (modification: Modification): modification is InitialModification =>
  modification.type === 'initial';
